import React, { useEffect, useState, useCallback } from "react";
import {
  customerListId,
  addToCard,
  addProductToList,
} from "../../../server/ShoppingList";
import { NavLink, useParams } from "react-router-dom";
import showAlert, {
  showAlertWithConfirmCancel,
} from "../../../server/ShowAlert";
import { getCart } from "../../../server/CollectSite";
import { setPreload } from "../../../server/CartsOpen";
import { useDispatch, useSelector } from "react-redux";
import {
  handleCartResponse,
  getAddToCartErrorMessage,
} from "./UserShoppingList";
import {
  ArrowRight,
  ListChecks,
  Trash2,
  Plus,
  Minus,
  ShoppingCart,
  PackageSearch,
} from "lucide-react";
import LoadingStatus from "../../ui/LoadingStatus";

const getWeightJump = (prod) => {
  const jump = parseInt(prod.product?.weightJump);
  return jump > 0 ? jump : 1;
};

const UserShoppingListProduct = ({ setProductPopUp, setSelectedProd }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const productsInCart = useSelector(
    (state) => state.productsInCart?.productsInCart ?? [],
  );
  const stationId = useSelector((state) => state.user?.stationId);
  const [products, setProducts] = useState(null);
  const [listName, setListName] = useState("");
  const [listId, setListId] = useState("");
  const [notFound, setNotFound] = useState(false);

  const getProductList = useCallback(() => {
    customerListId(id, stationId)
      .then((x) => {
        if (x.data) {
          setListId(x.data.id);
          setListName(x.data.name);
          setProducts(x.data.items ?? []);
        }
      })
      .catch((e) => {
        if (e.response?.status === 404) {
          setNotFound(true);
          showAlert({ msgTitle: "הרשימה לא נמצאה", msgType: "error" });
        }
        console.error(e);
      });
  }, [id, stationId]);

  useEffect(() => {
    getProductList();
  }, [getProductList]);

  const AddToCard = () => {
    if (!products || products.length === 0) {
      showAlert({
        msgTitle: "אין מוצרים ברשימה",
        msgType: "error",
      });
      return;
    }

    addToCard(stationId, products, productsInCart)
      .then((x) => {
        if (!x.data) {
          showAlert({
            msgTitle: "הייתה בעיה בהוספת הרשימה לעגלת קניות",
            msgType: "error",
          });
          return;
        }
        const reservedItems = x.data?.reservedItems || [];
        handleCartResponse(
          reservedItems,
          products,
          dispatch,
          stationId,
          showAlert,
          getCart,
          x.data?.allFailed,
        );
      })
      .catch((e) => {
        console.error(e);

        if (e.response?.status === 409 || e.response?.status === 400) {
          showAlert({
            msgTitle:
              getAddToCartErrorMessage(e) || "המוצרים אינם זמינים במלאי",
            msgType: "error",
          });
        } else {
          showAlert({
            msgTitle: getAddToCartErrorMessage(e),
            msgType: "error",
          });
        }
      });
  };

  const AddToShopList = (quantity, productId, suppressAlert = false) => {
    dispatch(setPreload(true));

    addProductToList(listId, productId, quantity)
      .then((response) => {
        if (!response.data) {
          showAlert({ msgTitle: "הייתה בעיה בעדכון הרשימה", msgType: "error" });
          return;
        }

        if (response.data.status === "DELETED") {
          setProducts((prev) =>
            prev.filter((item) => item.productId !== productId),
          );
          if (!suppressAlert) {
            showAlert({ msgTitle: "המוצר נמחק מהרשימה", msgType: "error" });
          }
        } else if (response.data.status === "UPDATED") {
          setProducts((prev) =>
            prev.map((item) =>
              item.productId === productId ? { ...item, quantity } : item,
            ),
          );
          if (!suppressAlert) {
            showAlert({ msgTitle: "הכמות עודכנה", msgType: "success" });
          }
        }
      })
      .catch(() => {
        showAlert({ msgTitle: "שגיאה בשרת, נסה שנית", msgType: "error" });
      })
      .finally(() => dispatch(setPreload(false)));
  };

  const openProduct = (prod) => {
    setSelectedProd(prod.product);
    setProductPopUp(true);
  };

  const handleQuantityChange = (value, productId) => {
    const qty = Math.max(1, parseInt(value) || 1);
    setProducts((prev) =>
      prev.map((p) =>
        p.productId === productId ? { ...p, quantity: qty } : p,
      ),
    );
  };

  const handleQuantityBlur = (productId, quantity, weightJump) => {
    if (quantity % weightJump !== 0) {
      const rounded = Math.round(quantity / weightJump) * weightJump;
      const finalQty = Math.max(weightJump, rounded);
      showAlert({
        msgTitle: `המוצר נמכר בקפיצות של ${weightJump} יחידות, הכמות עודכנה ל-${finalQty}`,
        msgType: "info",
      });
      AddToShopList(finalQty, productId, true);
    } else {
      AddToShopList(quantity, productId, false);
    }
  };

  const handleIncrement = (prod) => {
    const weightJump = getWeightJump(prod);
    AddToShopList(prod.quantity + weightJump, prod.productId, true);
  };

  const handleDecrement = (prod) => {
    const weightJump = getWeightJump(prod);
    const newQty = Math.max(0, prod.quantity - weightJump);
    AddToShopList(newQty, prod.productId, true);
  };

  const confirmDeleteProduct = (prod) => {
    showAlertWithConfirmCancel({
      msgTitle: "האם למחוק את המוצר מהרשימה?",
      confirmText: "מחק",
      cancelText: "ביטול",
      onConfirm: () => AddToShopList(0, prod.productId),
    });
  };

  const totalItems =
    products?.reduce((sum, p) => sum + (p.quantity || 0), 0) ?? 0;

  return (
    <div className="shopping-list-product">
      <div className="shopping-list-product__topbar">
        <NavLink className="shopping-list-product__back" to="/shopping-list">
          <ArrowRight size={16} strokeWidth={2.2} aria-hidden />
          חזרה לרשימות שלי
        </NavLink>
      </div>

      {products === null && !notFound ? (
        <LoadingStatus className="loading-status--compact" />
      ) : notFound ? (
        <div className="shopping-list-product__state">
          <PackageSearch size={44} strokeWidth={1.5} aria-hidden />
          <h2>הרשימה לא נמצאה</h2>
          <NavLink
            className="shopping-list-product__cta-link"
            to="/shopping-list"
          >
            לרשימות שלי
          </NavLink>
        </div>
      ) : (
        <>
          <div className="shopping-list-product__header">
            <span className="shopping-list-product__icon" aria-hidden>
              <ListChecks size={20} strokeWidth={2} />
            </span>
            <h1>{listName}</h1>
            {products.length > 0 && (
              <span className="shopping-list-product__count">
                {totalItems} פריטים
              </span>
            )}
          </div>

          {products.length > 0 ? (
            <>
              <div className="shopping-list-product__items">
                {products.map((prod) => {
                  const weightJump = getWeightJump(prod);

                  return (
                    <div className="product-row" key={prod.productId}>
                      <button
                        type="button"
                        className="product-row__name"
                        onClick={() => openProduct(prod)}
                      >
                        {prod.product ? prod.product.name : prod.productId}
                      </button>

                      <div className="product-row__actions">
                        <div className="qty-stepper">
                          <button
                            type="button"
                            className="qty-stepper__btn"
                            onClick={() => handleDecrement(prod)}
                            aria-label="הפחת כמות"
                          >
                            <Minus size={14} />
                          </button>
                          <input
                            type="number"
                            className="qty-stepper__input"
                            value={prod.quantity}
                            onChange={(e) =>
                              handleQuantityChange(
                                e.target.value,
                                prod.productId,
                              )
                            }
                            onBlur={(e) =>
                              handleQuantityBlur(
                                prod.productId,
                                Math.max(
                                  weightJump,
                                  parseInt(e.target.value) || weightJump,
                                ),
                                weightJump,
                              )
                            }
                            min={weightJump}
                            step={weightJump}
                            aria-label="כמות"
                          />
                          <button
                            type="button"
                            className="qty-stepper__btn"
                            onClick={() => handleIncrement(prod)}
                            aria-label="הוסף כמות"
                          >
                            <Plus size={14} />
                          </button>
                        </div>

                        <button
                          type="button"
                          className="product-row__delete"
                          onClick={() => confirmDeleteProduct(prod)}
                          aria-label="מחק מוצר"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="shopping-list-product__footer">
                <button className="btn-add-to-cart" onClick={AddToCard}>
                  <ShoppingCart size={18} aria-hidden />
                  הוספה לסל קניות
                </button>
              </div>
            </>
          ) : (
            <div className="shopping-list-product__state">
              <PackageSearch size={44} strokeWidth={1.5} aria-hidden />
              <h2>אין פריטים ברשימה</h2>
              <p>הוסיפו מוצרים לרשימה מדף המוצר כדי לראות אותם כאן</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserShoppingListProduct;
